import { getCurrentCxPlan } from "@jugl-web/domain-resources/subscriptions/utils";
import {
  useRestApiProvider,
  SubscriptionGetDetailedResponse,
  SubscriptionGetLiteResponse,
  PlanInfo,
  AddonInfo,
  PendingInvoice,
  getPendingInvoiceType,
  getSubscriptionProvider,
  getShopifyStoreHandle,
  isShopifyInactiveSubscriptionResponse,
} from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useTranslations, useToast } from "@jugl-web/utils";
import { Alert, PageLoaderFull } from "@jugl-web/ui-kit";
import { AddAITeamMembers } from "@web-src/modules/cx/components/AddAITeamMembers";
import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import { SelectedPlanInfo } from "@web-src/modules/cx/components/CompletePurchaseDialog/components/SummaryStep/types";
import { CancelPlanAlertContent } from "@web-src/modules/cx/components/CancelPlanAlertContent";
import { WorkspaceSubscriptionDrawer } from "@web-src/modules/cx/components/WorkspaceSubscriptionDrawer/WorkspaceSubscriptionDrawer";
import { CompletePurchaseDialog } from "@web-src/modules/cx/components/CompletePurchaseDialog";
import {
  UpdateCardV2Alert,
  UpdateCardV2AlertHandle,
} from "@web-src/modules/cx/components/UpdateCardV2Alert";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { ANONYMOUS_SESSION_PLAN_INFO } from "@jugl-web/utils/storage";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { waitForEntityLiveUpdate } from "@web-src/modules/socket/entityLiveUpdates";
import { SubscriptionTags } from "@jugl-web/rest-api/subscription/tags";
import {
  SubscriptionV2Context,
  SubscriptionV2ContextValue,
  EntitySubscription,
  AddonLineItem,
} from "./SubscriptionV2Context";

export const SubscriptionV2Provider: FC<PropsWithChildren> = ({ children }) => {
  const { subscriptionInfo, entity } = useEntityProvider();
  const entityId = entity?.id;
  const { subscriptionV2Api, integrationsApi } = useRestApiProvider();
  const { t } = useTranslations();
  const { show: showChatBot } = useChatBot();
  const showAlert = useShowAlert();

  const { isAnonymous } = useAnonymousSession();
  const dispatch = useDispatch();

  // Invalidate subscription cache when entity changes
  const previousEntityIdRef = useRef(entityId);
  useEffect(() => {
    if (
      entityId &&
      previousEntityIdRef.current &&
      entityId !== previousEntityIdRef.current
    ) {
      dispatch(
        subscriptionV2Api.util.invalidateTags([
          SubscriptionTags.subscriptionDetails,
          SubscriptionTags.plans,
          SubscriptionTags.addons,
          SubscriptionTags.invoices,
          SubscriptionTags.upcomingInvoices,
        ])
      );
    }
    previousEntityIdRef.current = entityId;
  }, [entityId, dispatch, subscriptionV2Api]);

  // Drawer state
  const [isSubscriptionDrawerOpen, setIsSubscriptionDrawerOpen] =
    useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [selectedPlanInfo, setSelectedPlanInfo] = useState<
    SelectedPlanInfo | undefined
  >();
  const selectPlanCallbackRef = useRef<
    ((planInfo: SelectedPlanInfo) => void) | null
  >(null);
  const [, setSelectedPlan] = useSessionStorage<SelectedPlanInfo | undefined>(
    ANONYMOUS_SESSION_PLAN_INFO,
    undefined
  );

  const isShopifySourced = entity?.metadata?.source === "shopify";

  const shouldFetchCxSubscription =
    !!entityId && !isAnonymous && entity?.type === "cx" && !isShopifySourced;

  const {
    data: subscriptionData,
    refetch: refetchSubscription,
    isLoading: isSubscriptionLoading,
  } = subscriptionV2Api.useGetSubscriptionQuery(
    shouldFetchCxSubscription
      ? { entityId, params: { mode: "detailed" } }
      : skipToken
  );

  const { data: liteSubscriptionData } =
    subscriptionV2Api.useGetSubscriptionQuery(
      shouldFetchCxSubscription
        ? { entityId, params: { mode: "lite" } }
        : skipToken
    );

  // RTK Query keeps `data` from the previous arg until the new request lands,
  // so both checks must be scoped to the selected workspace — otherwise a
  // Shopify workspace's payload gates the workspace switched into next
  const isDetailedShopifyInactive =
    isShopifyInactiveSubscriptionResponse(subscriptionData) &&
    subscriptionData.entity_id === entityId;

  const isLiteShopifyInactive =
    isShopifyInactiveSubscriptionResponse(liteSubscriptionData) &&
    liteSubscriptionData.entity_id === entityId;

  const isShopifySubscriptionPending =
    isDetailedShopifyInactive || isLiteShopifyInactive;

  // Inactive Shopify payload must never leak as a typed full subscription
  const subscription =
    !isShopifySubscriptionPending &&
    (subscriptionData as SubscriptionGetDetailedResponse | undefined)
      ?.entity_id === entityId
      ? (subscriptionData as SubscriptionGetDetailedResponse)
      : undefined;

  const liteSubscription = !isShopifySubscriptionPending
    ? (liteSubscriptionData as SubscriptionGetLiteResponse | undefined)
    : undefined;

  const subscriptionRef = useRef(subscription);
  const liteSubscriptionRef = useRef(liteSubscription);

  useEffect(() => {
    subscriptionRef.current = subscription;
    liteSubscriptionRef.current = liteSubscription;
  }, [subscription, liteSubscription]);

  const awaitSubscriptionUpdate = useCallback(
    async (liveUpdate: Promise<void>, isChanged: () => boolean) => {
      try {
        await liveUpdate;
      } catch {
        dispatch(
          subscriptionV2Api.util.invalidateTags([
            SubscriptionTags.subscriptionDetails,
          ])
        );
      }

      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (isChanged()) return true;
        // oxlint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
      }

      return isChanged();
    },
    [dispatch, subscriptionV2Api]
  );

  const pendingInvoices = useMemo(
    () => liteSubscription?.pending_invoices ?? [],
    [liteSubscription?.pending_invoices]
  );
  const hasPaymentFailed = pendingInvoices.some(
    (i) => getPendingInvoiceType(i) === "plan"
  );

  const subscriptionStatus = subscription?.status ?? liteSubscription?.status;
  const isSubscriptionCanceled =
    subscriptionStatus === "canceled" ||
    subscriptionStatus === "incomplete_expired";

  const billingProvider = getSubscriptionProvider(
    subscription ?? liteSubscription
  );

  const isShopifyBilling =
    isShopifySubscriptionPending || billingProvider === "shopify";

  // For an active Shopify-billed subscription the store comes with the
  // subscription details; before the first Shopify payment fall back to the
  // shopify connection (its name is provisionally the store handle)
  const { data: shopifyConnectionsData } =
    integrationsApi.useGetConnectionsQuery(
      entityId && isShopifyBilling ? { entityId, type: "shopify" } : skipToken
    );
  const shopifyStoreHandle =
    getShopifyStoreHandle(entity?.metadata?.store) ??
    getShopifyStoreHandle(subscription?.details?.plan?.store) ??
    shopifyConnectionsData?.data?.[0]?.name;

  const openShopifyPricingPage = useCallback(() => {
    const shopifyBillingRedirectUrl = isDetailedShopifyInactive
      ? subscriptionData.redirect_url
      : isLiteShopifyInactive
        ? liteSubscriptionData.redirect_url
        : undefined;

    // Prefer the backend-provided redirect when the inactive Shopify payload
    // is present; otherwise fall back to the constructed admin pricing URL.
    const pricingUrl =
      shopifyBillingRedirectUrl ??
      (shopifyStoreHandle
        ? `https://admin.shopify.com/store/${shopifyStoreHandle}/charges/jugl/pricing_plans`
        : undefined);

    if (!pricingUrl) {
      return;
    }

    // TODO(shopify-billing): confirm the app handle ("jugl") is the same
    // across environments (dev/staging apps may differ) for the fallback URL
    window.open(pricingUrl, "_blank", "noopener,noreferrer");
  }, [
    isDetailedShopifyInactive,
    isLiteShopifyInactive,
    liteSubscriptionData,
    shopifyStoreHandle,
    subscriptionData,
  ]);

  const openShopifyAdminStore = useCallback(() => {
    if (!shopifyStoreHandle) {
      return;
    }

    window.open(
      `https://admin.shopify.com/store/${shopifyStoreHandle}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, [shopifyStoreHandle]);

  const isSubscriptionPendingCancel =
    !!subscription?.cancel_at && !isSubscriptionCanceled;

  const { data: plansData, isLoading: isPlansLoading } =
    subscriptionV2Api.useListPlansQuery(
      entityId && entity.type === "cx"
        ? { entityId, params: { type: "cx" } }
        : skipToken
    );
  const plans = plansData?.data;

  const { currentPlan, currentPlanId } = useMemo(() => {
    const fromLineItems = getCurrentCxPlan({ subscription, plans });
    if (fromLineItems.currentPlan || !isShopifyBilling) {
      return fromLineItems;
    }
    // TODO(shopify-billing): provisional — Shopify subscriptions carry no
    // line_items, only a plan_handle; match it against the plan name until
    // backend provides a proper mapping (HANDOFF Q9)
    const planHandle =
      subscription?.details?.plan?.app_subscription?.plan_handle;
    const matchedPlan = planHandle
      ? plans?.find((p) => p.name.toLowerCase() === planHandle.toLowerCase())
      : undefined;
    return { currentPlan: matchedPlan, currentPlanId: matchedPlan?.id };
  }, [plans, subscription, isShopifyBilling]);

  const freePlan = useMemo(
    () => plans?.find((plan) => plan.meta_data.idx === 0),
    [plans]
  );

  const isFree = (currentPlan?.meta_data.idx ?? 0) === 0;

  const maxRollOverCredits =
    (liteSubscription?.entitlements?.["jugl::ai_credits"] ?? 0) * 2 || 0;

  const planDefaultCredits =
    currentPlan?.default_addons?.find((a) => a.id === "jugl::ai_credits")
      ?.credits ??
    freePlan?.default_addons?.find((a) => a.id === "jugl::ai_credits")
      ?.credits ??
    0;

  const isMonthly = useMemo(
    () =>
      // Shopify billing is monthly-only (interval "every_30_days")
      isShopifyBilling ||
      (subscription?.details?.plan?.line_items?.some(
        (item) => item.interval === "month"
      ) ??
        false),
    [subscription, isShopifyBilling]
  );

  // Build EntitySubscription from raw data
  const entitySubscription = useMemo<EntitySubscription | undefined>(() => {
    if (!subscription) return undefined;

    const appSubscription = subscription.details?.plan?.app_subscription;
    if (appSubscription) {
      // provider="shopify" — no Stripe line items; map from the Shopify
      // AppSubscription instead
      return {
        status: subscription.status,
        planName: appSubscription.name,
        planId: currentPlanId ?? "",
        billingInterval: "month",
        currentPeriodEnd: subscription.current_period_end,
        currentPeriodStart: subscription.current_period_start,
        // TODO(shopify-billing): confirm price format — assumed a "9.00"-style
        // string in major units, normalized here to minor units like Stripe
        totalPrice:
          Math.round(parseFloat(appSubscription.price || "0") * 100) || 0,
        addonLineItems: {},
        raw: subscription,
      };
    }

    const lineItems = subscription.details?.plan?.line_items ?? [];

    const planLineItem = lineItems.find((item) => item.info.kind === "plan");
    const planInfo = planLineItem?.info as PlanInfo | undefined;

    const addonLineItems: Record<string, AddonLineItem> = {};
    let totalPrice = 0;

    lineItems.forEach((item) => {
      totalPrice += item.unit_amount || 0;
      if (item.info.kind === "addon") {
        const addonInfo = item.info as AddonInfo;
        addonLineItems[addonInfo.addon_name] = {
          addonName: addonInfo.addon_name,
          quantity: item.quantity,
          subscriptionItemId: item.subscription_item_id,
          priceId: item.price_id,
          unitAmount: item.unit_amount,
        };
      }
    });

    return {
      status: subscription.status,
      planName: planInfo?.name ?? "",
      planId: planInfo?.plan_id ?? "",
      billingInterval: (planLineItem?.interval === "year"
        ? "year"
        : "month") as "month" | "year",
      currentPeriodEnd: subscription.current_period_end,
      currentPeriodStart: subscription.current_period_start,
      totalPrice,
      addonLineItems,
      raw: subscription,
    };
  }, [subscription, currentPlanId]);

  // Fetch supported addons for the current plan
  const { data: addonsData } = subscriptionV2Api.useListAddonsQuery(
    entityId && currentPlanId
      ? { entityId, params: { plan_id: currentPlanId } }
      : skipToken
  );

  const userCount = subscription?.entitlements?.["jugl::users"] ?? 0;

  const defaultUserCredits = useMemo(() => {
    const defaultAddon = currentPlan?.default_addons?.find(
      (a) => a.id === "jugl::users"
    );
    return defaultAddon?.credits ?? 0;
  }, [currentPlan]);

  const defaultAgentKnowledge = useMemo(() => {
    const defaultAddon =
      currentPlan?.default_addons?.find((a) => a.id === "jugl::knowledge") ??
      freePlan?.default_addons?.find((a) => a.id === "jugl::knowledge");
    return defaultAddon?.credits ?? 0;
  }, [currentPlan, freePlan]);

  const extraUserCount = Math.max(0, userCount - defaultUserCredits);

  const seatUsage = liteSubscription?.quota?.["jugl::users"]?.usage ?? 0;

  const isLoading = isSubscriptionLoading || isPlansLoading;

  // Card update alert
  const setupCardAlertRef = useRef<UpdateCardV2AlertHandle>(null);
  const hasPaymentMethod = !!subscription?.payment_method;

  const updateCardDetails = useCallback(async () => {
    if (isShopifyBilling) {
      // No Stripe payment method to manage — billing is handled by Shopify
      return { result: "cancelled" as const };
    }
    if (!setupCardAlertRef.current) {
      return { result: "apiError" as const };
    }
    return setupCardAlertRef.current.start();
  }, [isShopifyBilling]);

  // Seat adjustment
  const [isAdjustSeatsOpen, setIsAdjustSeatsOpen] = useState(false);
  const { toast } = useToast();
  const [adjustAddon, { isLoading: isAdjustLoading }] =
    subscriptionV2Api.useAdjustAddonMutation();

  const adjustSeats = useCallback(() => {
    if (isShopifyBilling) {
      // Shopify billing has no addons — seats are fixed by the plan
      return;
    }
    if (isSubscriptionPendingCancel) {
      showChatBot();
      return;
    }
    setIsAdjustSeatsOpen(true);
  }, [isShopifyBilling, isSubscriptionPendingCancel, showChatBot]);

  const seatAddonPrice = useMemo(() => {
    const usersAddon = addonsData?.data?.find((a) => a.id === "jugl::users");
    const matchingPrice = usersAddon?.prices[0];
    return matchingPrice
      ? { priceId: matchingPrice.id, amount: matchingPrice.unit_amount / 100 }
      : undefined;
  }, [addonsData]);

  // --- Payment failed alert (shared for plan upgrade) ---
  const showPaymentFailedAlert = useCallback(
    (pendingInvoice?: PendingInvoice) => {
      showAlert({
        title: "Payment Failed",
        content: pendingInvoice
          ? "Payment require additional action"
          : "Payment failed. Please check your card details and try again.",
        buttons: pendingInvoice
          ? [
              { text: "Close", color: "grey", role: "close" },
              {
                text: "Complete Payment",
                color: "primary",
                onClick: (_, { closeAlert }) => {
                  closeAlert();
                  window.open(pendingInvoice.invoice_url, "_blank");
                },
              },
            ]
          : [
              { text: "Close", color: "grey", role: "close" },
              {
                text: "Update Card",
                color: "primary",
                onClick: async (_, { closeAlert }) => {
                  closeAlert();
                  await updateCardDetails();
                },
              },
            ],
      });
    },
    [showAlert, updateCardDetails]
  );

  // --- Change plan with async polling ---
  const [changePlan] = subscriptionV2Api.useChangePlanMutation();
  const [isChangePlanInProgress, setIsChangePlanInProgress] = useState(false);

  const upgradeSubscriptionPlan = useCallback(
    async (params: {
      planId: string;
      priceId: string;
      planName: string;
      onSuccess: () => void;
      onFailure?: (pendingInvoice?: PendingInvoice) => void;
    }) => {
      if (!entityId) return;
      if (isShopifyBilling) {
        // Plan changes for Shopify-billed workspaces happen on Shopify's
        // pricing page, not through the Stripe change_plan flow
        openShopifyPricingPage();
        return;
      }
      const handleFailure = params.onFailure ?? showPaymentFailedAlert;
      setIsChangePlanInProgress(true);

      // Subscribe before the mutation so a fast lobby event is not missed.
      const liveUpdate = waitForEntityLiveUpdate("subscription_update");

      const findPlanInvoice = () =>
        liteSubscriptionRef.current?.pending_invoices?.find(
          (i) => getPendingInvoiceType(i) === "plan"
        );

      const hasNewPlan = () => {
        const planInfo =
          subscriptionRef.current?.details?.plan?.line_items?.find(
            (item) => item.info.kind === "plan"
          )?.info;

        return planInfo?.kind === "plan" && planInfo.plan_id === params.planId;
      };

      try {
        await changePlan({
          entityId,
          data: { plan_id: params.planId, price_id: params.priceId },
        }).unwrap();

        await awaitSubscriptionUpdate(
          liveUpdate,
          () => !!findPlanInvoice() || hasNewPlan()
        );

        setIsChangePlanInProgress(false);

        const planInvoice = findPlanInvoice();
        if (planInvoice) {
          handleFailure(planInvoice);
          return;
        }

        if (hasNewPlan()) {
          params.onSuccess();
          return;
        }

        handleFailure();
      } catch (error) {
        liveUpdate.catch(() => undefined);
        setIsChangePlanInProgress(false);
        showPaymentFailedAlert();
        throw error;
      }
    },
    [
      entityId,
      isShopifyBilling,
      openShopifyPricingPage,
      changePlan,
      awaitSubscriptionUpdate,
      showPaymentFailedAlert,
    ]
  );

  // --- Addon purchase with async polling ---
  const [isAddonPurchaseInProgress, setIsAddonPurchaseInProgress] =
    useState(false);

  const handleBuyTeamMembers = useCallback(
    async (newQuantity: number) => {
      if (!entityId || !seatAddonPrice) return;

      // Subscribe before the mutation so a fast lobby event is not missed.
      const liveUpdate = waitForEntityLiveUpdate("subscription_update");

      // Capture user count before the change. Both sides of the comparison must
      // read the lite payload: `userCount` comes from the detailed response, and
      // any disagreement between the two would read as "the seat count changed"
      // on the very first check.
      const usersBefore =
        liteSubscriptionRef.current?.entitlements?.["jugl::users"] ?? 0;

      const findSeatInvoice = () =>
        liteSubscriptionRef.current?.pending_invoices?.find(
          (i) => getPendingInvoiceType(i) === "addon-seats"
        );

      const hasNewSeatCount = () =>
        (liteSubscriptionRef.current?.entitlements?.["jugl::users"] ?? 0) !==
        usersBefore;

      try {
        await adjustAddon({
          entityId,
          data: { price_id: seatAddonPrice.priceId, qty: newQuantity },
        }).unwrap();
        setIsAdjustSeatsOpen(false);
        setIsAddonPurchaseInProgress(true);

        await awaitSubscriptionUpdate(
          liveUpdate,
          () => !!findSeatInvoice() || hasNewSeatCount()
        );

        setIsAddonPurchaseInProgress(false);

        if (findSeatInvoice()) {
          toast(
            t({
              id: "cx-plans-page.payment-failed",
              defaultMessage:
                "Payment failed. Please check your card details and try again.",
            }),
            { variant: "error" }
          );
          return;
        }

        if (hasNewSeatCount()) {
          toast(
            t({
              id: "cx-plans-page.team-members-updated",
              defaultMessage: "Team members updated successfully",
            }),
            { variant: "success" }
          );
          return;
        }

        showAlert({
          title: t({
            id: "cx-plans-page.addon-processing-title",
            defaultMessage: "Payment Processing",
          }),
          content: t({
            id: "cx-plans-page.addon-processing-content",
            defaultMessage:
              "Payment processing is taking longer than expected. Please refresh the page later or contact support.",
          }),
          buttons: [
            {
              text: t({ id: "common.ok", defaultMessage: "OK" }),
              color: "primary",
              role: "close",
            },
          ],
        });
      } catch {
        liveUpdate.catch(() => undefined);
        setIsAddonPurchaseInProgress(false);
      }
    },
    [
      entityId,
      seatAddonPrice,
      adjustAddon,
      awaitSubscriptionUpdate,
      toast,
      t,
      showAlert,
    ]
  );

  // --- Credit topup with async polling ---
  const [topupNow] = subscriptionV2Api.useTopupNowMutation();
  const [isTopupInProgress, setIsTopupInProgress] = useState(false);

  const topupCredits = useCallback(
    async (params: {
      amountInCents: number;
      onSuccess: () => void;
      onOpenInvoice: (invoiceUrl: string) => void;
    }) => {
      if (!entityId) return;
      if (isShopifyBilling) {
        // No recharge/top-up for Shopify-billed workspaces
        return;
      }

      setIsTopupInProgress(true);

      // Subscribe before the mutation so a fast lobby event is not missed.
      const liveUpdate = waitForEntityLiveUpdate("quota_update");

      // Capture total credits before topup
      const creditsBefore =
        liteSubscriptionRef.current?.quota?.["jugl::ai_credits"];
      const totalBefore =
        (creditsBefore?.allowed ?? 0) + (creditsBefore?.balance ?? 0);

      try {
        const result = await topupNow({
          entityId,
          data: { amount: params.amountInCents },
        }).unwrap();

        // If invoice needs manual payment, no polling
        if (result.status === "open") {
          liveUpdate.catch(() => undefined);
          setIsTopupInProgress(false);
          params.onOpenInvoice(result.invoice_url);
          return;
        }

        const hasNewCredits = await awaitSubscriptionUpdate(liveUpdate, () => {
          const creditsAfter =
            liteSubscriptionRef.current?.quota?.["jugl::ai_credits"];

          return (
            (creditsAfter?.allowed ?? 0) + (creditsAfter?.balance ?? 0) >
            totalBefore
          );
        });

        setIsTopupInProgress(false);

        if (hasNewCredits) {
          params.onSuccess();
          return;
        }

        showAlert({
          title: t({
            id: "cx-plans-page.topup-processing-title",
            defaultMessage: "Payment Processing",
          }),
          content: t({
            id: "cx-plans-page.topup-processing-content",
            defaultMessage:
              "Payment processing is taking longer than expected. Please refresh the page later and go to Manage Subscription to check.",
          }),
          buttons: [
            {
              text: t({
                id: "common.ok",
                defaultMessage: "OK",
              }),
              color: "primary",
              role: "close",
            },
          ],
        });
      } catch (error) {
        liveUpdate.catch(() => undefined);
        setIsTopupInProgress(false);
        throw error;
      }
    },
    [
      entityId,
      isShopifyBilling,
      topupNow,
      awaitSubscriptionUpdate,
      showAlert,
      t,
    ]
  );

  const [cancelSubscriptionMutation] =
    subscriptionV2Api.useCancelSubscriptionMutation();

  const [isExtraCreditsDialogRequested, setIsExtraCreditsDialogRequested] =
    useState(false);
  const requestExtraCreditsDialog = useCallback(
    () => setIsExtraCreditsDialogRequested(true),
    []
  );
  const clearExtraCreditsDialogRequest = useCallback(
    () => setIsExtraCreditsDialogRequested(false),
    []
  );

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const cancelSubscription = useCallback(() => {
    if (isShopifyBilling) {
      // TODO(shopify-billing): cancel flow for Shopify subscriptions is
      // unclear (via Shopify admin / app uninstall?) — no-op for now
      return;
    }
    setIsCancelDialogOpen(true);
  }, [isShopifyBilling]);

  const [isCancelInProgress, setIsCancelInProgress] = useState(false);

  const handleCancelDowngrade = useCallback(
    async (feedback: string) => {
      setIsCancelDialogOpen(false);
      if (!entityId) return;
      setIsCancelInProgress(true);

      const liveUpdate = waitForEntityLiveUpdate("subscription_update");

      try {
        await cancelSubscriptionMutation({
          entityId,
          data: feedback ? { feedback } : { feedback: "no-feedback" },
          meta: {
            successMessage: t({
              id: "cx-plans-page.subscription-canceled",
              defaultMessage: "Subscription canceled successfully",
            }),
          },
        }).unwrap();

        await awaitSubscriptionUpdate(
          liveUpdate,
          () =>
            !!liteSubscriptionRef.current?.cancel_at ||
            liteSubscriptionRef.current?.status === "canceled"
        );
      } catch {
        liveUpdate.catch(() => undefined);
      } finally {
        setIsCancelInProgress(false);
      }
    },
    [entityId, cancelSubscriptionMutation, awaitSubscriptionUpdate, t]
  );

  const reactivateCurrentPlan = useCallback(() => {
    if (isShopifyBilling) {
      // Reactivation for Shopify-billed workspaces happens on Shopify's side
      openShopifyPricingPage();
      return;
    }
    if (!currentPlan) return;
    const interval = isMonthly ? "month" : "year";
    const priceId = currentPlan.prices.find((p) => p.interval === interval)?.id;
    if (!priceId) return;

    const planName = currentPlan.name;
    const intervalLabel = isMonthly ? "Monthly" : "Yearly";

    showAlert({
      title: "Reactivate your plan?",
      content: (
        <div className="text-center text-dark-700">
          Are you sure you want to reactivate your{" "}
          <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
            {planName}
          </span>{" "}
          plan? Your subscription will continue on the current {intervalLabel}{" "}
          billing cycle.
        </div>
      ),
      buttons: [
        { text: "Cancel", color: "grey", role: "close" },
        {
          text: "Reactivate Plan",
          color: "primary",
          onClick: (_, { closeAlert }) => {
            closeAlert();
            upgradeSubscriptionPlan({
              planId: currentPlan.id,
              priceId,
              planName,
              onSuccess: () => {
                window.location.reload();
              },
            }).catch(() => {
              // Provider already surfaces payment-failed alert internally
            });
          },
        },
      ],
    });
  }, [
    isShopifyBilling,
    openShopifyPricingPage,
    currentPlan,
    isMonthly,
    showAlert,
    upgradeSubscriptionPlan,
  ]);

  // Drawer control methods
  // NOTE: every purchase/upgrade entry point in the app funnels through
  // openSubscriptionDrawer/openPurchaseDialog, so redirecting here keeps the
  // whole Stripe purchase flow unreachable for Shopify-billed workspaces
  const openSubscriptionDrawer = useCallback(
    (config?: { onSelectPlan?: (planInfo: SelectedPlanInfo) => void }) => {
      if (isShopifyBilling) {
        openShopifyPricingPage();
        return;
      }
      selectPlanCallbackRef.current = config?.onSelectPlan ?? null;
      setIsSubscriptionDrawerOpen(true);
    },
    [isShopifyBilling, openShopifyPricingPage]
  );

  const closeSubscriptionDrawer = useCallback(() => {
    setIsSubscriptionDrawerOpen(false);
    selectPlanCallbackRef.current = null;
  }, []);

  const openPurchaseDialog = useCallback(
    (planInfo: SelectedPlanInfo) => {
      if (isShopifyBilling) {
        openShopifyPricingPage();
        return;
      }
      setSelectedPlanInfo(planInfo);
      setIsPurchaseDialogOpen(true);
    },
    [isShopifyBilling, openShopifyPricingPage]
  );

  const closePurchaseDialog = useCallback(() => {
    setIsPurchaseDialogOpen(false);
    setSelectedPlanInfo(undefined);
    setSelectedPlan(undefined);
  }, [setSelectedPlan]);

  const handleSelectPlan = useCallback(
    (planInfo: SelectedPlanInfo) => {
      if (selectPlanCallbackRef.current) {
        selectPlanCallbackRef.current(planInfo);
      } else {
        setIsSubscriptionDrawerOpen(false);
        openPurchaseDialog(planInfo);
      }
    },
    [openPurchaseDialog]
  );

  const handleDowngrade = useCallback(
    (planInfo: SelectedPlanInfo) => {
      setIsSubscriptionDrawerOpen(false);
      openPurchaseDialog(planInfo);
    },
    [openPurchaseDialog]
  );

  const value: SubscriptionV2ContextValue = useMemo(
    () => ({
      subscription,
      liteSubscription,
      entitySubscription,
      plans,
      supportedAddons: addonsData?.data,
      currentPlan,
      currentPlanId,
      userCount,
      extraUserCount,
      seatUsage,
      pendingInvoices,
      hasPaymentFailed,
      isSubscriptionCanceled,
      isSubscriptionPendingCancel,
      defaultAgentKnowledge,
      isFree,
      isMonthly,
      billingProvider,
      isShopifyBilling,
      isShopifySubscriptionPending,
      shopifyStoreHandle,
      openShopifyPricingPage,
      openShopifyAdminStore,
      isLoading,
      refetchSubscription,
      cancelSubscription,
      reactivateCurrentPlan,
      openSubscriptionDrawer,
      closeSubscriptionDrawer,
      openPurchaseDialog,
      closePurchaseDialog,
      updateCardDetails,
      hasPaymentMethod,
      adjustSeats,
      upgradeSubscriptionPlan,
      isChangePlanInProgress,
      isAddonPurchaseInProgress,
      isExtraCreditsDialogRequested,
      requestExtraCreditsDialog,
      clearExtraCreditsDialogRequest,
      topupCredits,
      isTopupInProgress,
      maxRollOverCredits,
      planDefaultCredits,
    }),
    [
      defaultAgentKnowledge,
      subscription,
      liteSubscription,
      entitySubscription,
      plans,
      addonsData,
      currentPlan,
      currentPlanId,
      userCount,
      extraUserCount,
      seatUsage,
      pendingInvoices,
      hasPaymentFailed,
      isSubscriptionCanceled,
      isSubscriptionPendingCancel,
      isFree,
      isMonthly,
      billingProvider,
      isShopifyBilling,
      isShopifySubscriptionPending,
      shopifyStoreHandle,
      openShopifyPricingPage,
      openShopifyAdminStore,
      isLoading,
      refetchSubscription,
      cancelSubscription,
      reactivateCurrentPlan,
      openSubscriptionDrawer,
      closeSubscriptionDrawer,
      openPurchaseDialog,
      closePurchaseDialog,
      updateCardDetails,
      hasPaymentMethod,
      adjustSeats,
      upgradeSubscriptionPlan,
      isChangePlanInProgress,
      isAddonPurchaseInProgress,
      isExtraCreditsDialogRequested,
      requestExtraCreditsDialog,
      clearExtraCreditsDialogRequest,
      topupCredits,
      isTopupInProgress,
      maxRollOverCredits,
      planDefaultCredits,
    ]
  );

  return (
    <SubscriptionV2Context.Provider value={value}>
      {children}
      <PageLoaderFull
        isActive={
          isChangePlanInProgress ||
          isAddonPurchaseInProgress ||
          isCancelInProgress ||
          isTopupInProgress
        }
        isTransparent
      />
      {entity && subscriptionInfo && (
        <>
          <WorkspaceSubscriptionDrawer
            isOpen={isSubscriptionDrawerOpen}
            onClose={closeSubscriptionDrawer}
            currentPlanId={currentPlanId}
            currentInterval={isMonthly ? "month" : "year"}
            onDowngrade={handleDowngrade}
            onSelectPlan={handleSelectPlan}
          />
          <CompletePurchaseDialog
            isOpen={isPurchaseDialogOpen}
            planInfo={selectedPlanInfo}
            onClose={closePurchaseDialog}
          />
          <Alert
            isOpen={isCancelDialogOpen}
            onRequestClose={() => setIsCancelDialogOpen(false)}
            title="⚠️ Cancel Plan"
            contentContainerClassName="px-10 pt-10 pb-10"
            textAlignment="left"
            content={
              <CancelPlanAlertContent
                onCancel={() => setIsCancelDialogOpen(false)}
                onDowngrade={handleCancelDowngrade}
              />
            }
          />
          {/* Stripe-only surfaces — Shopify-billed workspaces manage cards
              and seats on Shopify's side */}
          {!isShopifyBilling && (
            <>
              <UpdateCardV2Alert ref={setupCardAlertRef} entityId={entity.id} />
              <AddAITeamMembers
                isOpen={isAdjustSeatsOpen}
                onClose={() => setIsAdjustSeatsOpen(false)}
                currentQuantity={extraUserCount}
                minQuantity={Math.max(
                  0,
                  (entity?.userCount ?? 0) - defaultUserCredits
                )}
                pricePerMember={seatAddonPrice?.amount ?? 25}
                isLoading={isAdjustLoading}
                onBuy={handleBuyTeamMembers}
              />
            </>
          )}
        </>
      )}
    </SubscriptionV2Context.Provider>
  );
};
